import { t, timestampCodec } from "../../validation/validator";
import { CallResponseMixinSchema } from "../common/callResponseSchema";
import { stripeDiscountSchema } from "./stripeDiscount.schemas";
import { stripeInvoiceSchema } from "./stripeInvoice.schemas";

// ─── Shared ──────────────────────────────────────────────────────────────────

export const stripeSubscriptionStatusSchema = t.union([
  t.literal("active"),
  t.literal("canceled"),
  t.literal("incomplete"),
  t.literal("incomplete_expired"),
  t.literal("past_due"),
  t.literal("paused"),
  t.literal("trialing"),
  t.literal("unpaid"),
]);
export type StripeSubscriptionStatus = t.TypeOf<typeof stripeSubscriptionStatusSchema>;

// ─── Database Record Schemas ─────────────────────────────────────────────────

const stripeSubscriptionCommonFields = {
  created_at: timestampCodec,
  updated_at: timestampCodec,
  status: stripeSubscriptionStatusSchema,
  canceled_at: t.union([timestampCodec, t.null, t.undefined]),
  current_period_start: t.union([timestampCodec, t.null, t.undefined]),
  current_period_end: t.union([timestampCodec, t.null, t.undefined]),
  payment_type: t.union([t.literal("one_time"), t.literal("recurring")]),
  payment_interval: t.union([t.literal("day"), t.literal("week"), t.literal("month"), t.literal("year")]),
  ended_at: t.union([timestampCodec, t.null, t.undefined]),
  trial_start: t.union([timestampCodec, t.null, t.undefined]),
  trial_end: t.union([timestampCodec, t.null, t.undefined]),
  product_id: t.string,
  price_id: t.string,
  quantity: t.union([t.number, t.null, t.undefined]),
};

export const stripeSubscriptionRecordSchema = t.type({
  ...stripeSubscriptionCommonFields,
  id: t.number,
  doc_id: t.string,
  user_id: t.number,
});
export type StripeSubscriptionRecord = t.TypeOf<typeof stripeSubscriptionRecordSchema>;

export const stripeSubscriptionRecordV2Schema = t.type({
  ...stripeSubscriptionCommonFields,
  id: t.number,
  account_id: t.number,
  subscription_id: t.string,
});
export type StripeSubscriptionRecordV2 = t.TypeOf<typeof stripeSubscriptionRecordV2Schema>;

export const stripeSubscriptionInsertSchema = t.type({
  ...stripeSubscriptionCommonFields,
  user_id: t.number,
  doc_id: t.string,
});
export type StripeSubscriptionInsert = t.TypeOf<typeof stripeSubscriptionInsertSchema>;

// ─── Price & Item ────────────────────────────────────────────────────────────

export const stripeSubscriptionPriceSchema = t.intersection([
  t.partial({
    tiers_mode: t.union([t.string, t.null]),
    currency_options: t.record(t.string, t.unknown),
  }),
  t.type({
    id: t.string,
    unit_amount: t.union([t.number, t.null]),
    currency: t.string,
    interval: t.union([t.literal("day"), t.literal("week"), t.literal("month"), t.literal("year"), t.null, t.undefined]),
    interval_count: t.union([t.number, t.null, t.undefined]),
    type: t.union([t.literal("one_time"), t.literal("recurring")]),
    recurring: t.type({
      interval: t.union([t.literal("month"), t.literal("year")]),
      interval_count: t.number,
    }),
    tiers: t.union([
      t.array(
        t.type({
          flat_amount: t.union([t.number, t.null]),
          flat_amount_decimal: t.union([t.string, t.number, t.null]),
          unit_amount: t.union([t.number, t.null]),
          unit_amount_decimal: t.union([t.string, t.number, t.null]),
          up_to: t.union([t.number, t.null]),
        })
      ),
      t.undefined,
    ]),
  }),
]);
export type StripeSubscriptionPrice = t.TypeOf<typeof stripeSubscriptionPriceSchema>;

export const stripeSubscriptionItemSchema = t.intersection([
  t.type({
    id: t.string,
    quantity: t.union([t.number, t.null]),
    price: stripeSubscriptionPriceSchema,
  }),
  t.partial({
    current_period_start: t.union([t.number, t.null]),
    current_period_end: t.union([t.number, t.null]),
  }),
]);
export type StripeSubscriptionItem = t.TypeOf<typeof stripeSubscriptionItemSchema>;

export const stripeCreateSubscriptionRequestSchema = t.intersection([
  t.type({
    user_id: t.string,
    price_id: t.string,
    return_url: t.string,
    currency: t.string,
    billing_details: t.type({
      name: t.string,
      address: t.type({
        line1: t.string,
        line2: t.union([t.string, t.null]),
        city: t.string,
        state: t.string,
        postal_code: t.string,
        country: t.string,
      }),
    }),
  }),
  t.partial({
    confirmation_token_id: t.string,
    promotion_code_id: t.string,
    quantity: t.number,
  }),
]);
export type StripeCreateSubscriptionRequest = t.TypeOf<typeof stripeCreateSubscriptionRequestSchema>;

const stripePaymentIntentStatusSchema = t.union([
  t.literal("canceled"),
  t.literal("processing"),
  t.literal("requires_action"),
  t.literal("requires_capture"),
  t.literal("requires_confirmation"),
  t.literal("requires_payment_method"),
  t.literal("succeeded"),
]);

// ─── Subscription Details ─────────────────────────────────────────────────

export const stripeSubscriptionSchedulePhaseSchema = t.type({
  start_date: t.number,
  items: t.array(
    t.partial({
      quantity: t.union([t.number, t.null]),
    })
  ),
});

export const stripeSubscriptionScheduleSchema = t.type({
  id: t.string,
  phases: t.array(stripeSubscriptionSchedulePhaseSchema),
});
export type StripeSubscriptionSchedule = t.TypeOf<typeof stripeSubscriptionScheduleSchema>;

export const stripeSubscriptionDetailsSchema = t.intersection([
  t.type({
    id: t.string,
    status: stripeSubscriptionStatusSchema,
    created: t.number,
    billing_cycle_anchor: t.number,
    cancel_at_period_end: t.boolean,
    canceled_at: t.union([t.number, t.null]),
    items: t.type({
      data: t.array(stripeSubscriptionItemSchema),
    }),
    latest_invoice: stripeInvoiceSchema,
    discounts: t.array(t.union([stripeDiscountSchema, t.string])),
    schedule: t.union([stripeSubscriptionScheduleSchema, t.null]),
    quantity: t.number,
  }),
  t.partial({
    customer: t.unknown,
    cancel_at: t.union([t.number, t.null]),
    billing_payment_method: t.union([t.type({ id: t.string, card: t.union([t.type({ exp_month: t.number, exp_year: t.number }), t.null]) }), t.null]),
    default_payment_method: t.union([t.unknown, t.null]),
  }),
]);
export type StripeSubscriptionDetails = t.TypeOf<typeof stripeSubscriptionDetailsSchema>;

export const createSubscriptionResponseSchema = t.type({
  status: stripePaymentIntentStatusSchema,
  clientSecret: t.string,
  subscription: stripeSubscriptionDetailsSchema,
});
export type StripeCreateSubscriptionResponse = t.TypeOf<typeof createSubscriptionResponseSchema>;

// ─── Subscription Requests ────────────────────────────────────────────────────

export const stripeGetSubscriptionDetailsRequestSchema = t.intersection([
  t.type({
    user_id: t.string,
  }),
  t.partial({
    include_incomplete: t.boolean,
  }),
]);
export type StripeGetSubscriptionDetailsRequest = t.TypeOf<typeof stripeGetSubscriptionDetailsRequestSchema>;

export const stripeGetSubscriptionDetailsResponseSchema = t.intersection([
  t.type({
    data: t.array(stripeSubscriptionDetailsSchema),
  }),
  CallResponseMixinSchema,
]);
export type StripeGetSubscriptionDetailsResponse = t.TypeOf<typeof stripeGetSubscriptionDetailsResponseSchema>;

export const stripeCancelScheduledSubscriptionUpdateRequestSchema = t.type({
  subscription_id: t.string,
});
export type StripeCancelScheduledSubscriptionUpdateRequest = t.TypeOf<typeof stripeCancelScheduledSubscriptionUpdateRequestSchema>;

export const stripeUpdateSubscriptionRequestSchema = t.intersection([
  t.type({
    subscription_id: t.string,
  }),
  t.partial({
    current_sub_item_id: t.string,
    new_price_id: t.string,
    quantity: t.number,
    promo_id: t.string,
    setup_intent_id: t.string,
    payment_method_id: t.string,
    billing_cycle_anchor: t.union([t.literal("now"), t.literal("unchanged")]),
    cancel_at_period_end: t.boolean,
  }),
]);
export type StripeUpdateSubscriptionRequest = t.TypeOf<typeof stripeUpdateSubscriptionRequestSchema>;

const stripePendingUpdateSchema = t.type({
  quantity: t.number,
  effective_at: t.number,
});

export const stripeUpdateSubscriptionDataSchema = t.union([
  t.type({
    id: t.string,
    status: t.string,
  }),
  t.type({
    id: t.string,
    status: t.string,
    schedule: t.string,
    pending_update: stripePendingUpdateSchema,
  }),
]);
